import { processStore, ProcessMessage } from "./processStore";
import { warn, debug } from "../utils/console";
import { originalFetch } from "../utils/fetchInterceptor";

export type ProgressConnectionMode =
  | "idle"
  | "connecting"
  | "sse"
  | "polling"
  | "reconnecting"
  | "degraded";

interface ServerConnection {
  port: number;
  sse: EventSource | null;
  pollTimer: NodeJS.Timeout | null;
  isConnected: boolean;
  lastTimestamp: number;
  pollFailures: number;
  connectionMode: ProgressConnectionMode;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
  reconnectAttempts: number;
  sseOpened: boolean;
}

const MAX_POLL_FAILURES_BEFORE_RECONNECT = 5;
const MAX_RECONNECT_ATTEMPTS = 20;
const SSE_CONNECT_TIMEOUT_MS = 5000;
const POLL_INTERVAL_MS = 3000;
const BASE_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 60000;
/** Coalesce high-frequency script/upload progress lines so the UI stays responsive. */
const PROGRESS_THROTTLE_MS = 250;
const PROGRESS_EVENTS = new Set<ProcessMessage["event"]>([
  "progress_event",
  "script_output",
  "upload_progress",
  "script_progress",
]);

class SSEManager {
  private connections = new Map<number, ServerConnection>();
  private cleanupTimers = new Map<number, number>();
  private messageListeners = new Set<(message: ProcessMessage) => void>();
  private connectionStateListeners = new Set<
    (state: Record<number, ProgressConnectionMode>) => void
  >();
  private activeProcesses = new Set<string>();
  private processTypeToPort = new Map<string, number>();
  private progressPending = new Map<string, ProcessMessage>();
  private progressTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private progressLastFlush = new Map<string, number>();

  constructor() {
    this.connections.set(8059, this.createConnection(8059));
    this.connections.set(8049, this.createConnection(8049));

    this.processTypeToPort.set("video_upload", 8059);
    this.processTypeToPort.set("script_execution", 8049);

    setInterval(() => {
      processStore.cleanupOldProcesses();
    }, 5 * 60 * 1000);
  }

  private createConnection(port: number): ServerConnection {
    return {
      port,
      sse: null,
      pollTimer: null,
      isConnected: false,
      lastTimestamp: 0,
      pollFailures: 0,
      connectionMode: "idle",
      reconnectTimer: null,
      reconnectAttempts: 0,
      sseOpened: false,
    };
  }

  private notifyConnectionState(): void {
    const state: Record<number, ProgressConnectionMode> = {};
    this.connections.forEach((conn, port) => {
      state[port] = conn.connectionMode;
    });
    this.connectionStateListeners.forEach((listener) => {
      try {
        listener(state);
      } catch (e) {
        warn("[SSEManager] Error in connection state listener:", e);
      }
    });
  }

  private setConnectionMode(port: number, mode: ProgressConnectionMode): void {
    const connection = this.connections.get(port);
    if (!connection || connection.connectionMode === mode) return;
    connection.connectionMode = mode;
    this.notifyConnectionState();
  }

  private hasActiveProcessesForPort(port: number): boolean {
    const processType = port === 8059 ? "video_upload" : "script_execution";
    return Array.from(this.activeProcesses).some((processId) => {
      const process = processStore.getProcess(processId);
      return process && process.type === processType && process.status === "running";
    });
  }

  private clearReconnectTimer(port: number): void {
    const connection = this.connections.get(port);
    if (!connection?.reconnectTimer) return;
    clearTimeout(connection.reconnectTimer);
    connection.reconnectTimer = null;
  }

  private scheduleReconnect(port: number): void {
    if (!this.hasActiveProcessesForPort(port)) {
      this.setConnectionMode(port, "idle");
      return;
    }

    const connection = this.connections.get(port);
    if (!connection) return;

    if (connection.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      warn(`[SSEManager] Reconnect attempts exhausted for port ${port}`);
      connection.isConnected = false;
      this.setConnectionMode(port, "degraded");
      return;
    }

    this.clearReconnectTimer(port);
    connection.reconnectAttempts += 1;
    const delay = Math.min(
      BASE_RECONNECT_DELAY_MS * 2 ** (connection.reconnectAttempts - 1),
      MAX_RECONNECT_DELAY_MS
    );

    debug(
      `[SSEManager] Scheduling reconnect for port ${port} in ${delay}ms (attempt ${connection.reconnectAttempts})`
    );
    this.setConnectionMode(port, "reconnecting");

    connection.reconnectTimer = setTimeout(() => {
      connection.reconnectTimer = null;
      void this.reconnectPort(port);
    }, delay);
  }

  private async reconnectPort(port: number): Promise<void> {
    if (!this.hasActiveProcessesForPort(port)) {
      this.setConnectionMode(port, "idle");
      return;
    }

    this.teardownTransport(port);
    await this.connectToServer(port);
  }

  private teardownTransport(port: number): void {
    const connection = this.connections.get(port);
    if (!connection) return;

    if (connection.sse) {
      try {
        connection.sse.close();
      } catch (e) {
        warn(`[SSEManager] Error closing SSE for port ${port}:`, e);
      }
      connection.sse = null;
    }

    if (connection.pollTimer) {
      clearInterval(connection.pollTimer);
      connection.pollTimer = null;
    }

    connection.sseOpened = false;
    connection.isConnected = false;
    connection.pollFailures = 0;
  }

  onProcessStart(processId: string, processType: "video_upload" | "script_execution") {
    debug(`[SSEManager] Process started: ${processId} (${processType})`);
    this.activeProcesses.add(processId);

    const port = this.processTypeToPort.get(processType);
    if (port) {
      if (
        !this.isConnected(port) ||
        connectionModeNeedsRecovery(this.connections.get(port)?.connectionMode)
      ) {
        void this.ensureConnection(port);
      }
    } else {
      warn(`[SSEManager] No port mapping found for process type: ${processType}`);
    }
  }

  onProcessComplete(processId: string, processType: "video_upload" | "script_execution") {
    this.activeProcesses.delete(processId);
    this.clearProgressThrottle(processId);

    const port = this.processTypeToPort.get(processType);
    if (port) {
      this.checkAndDisconnectIfNoActiveProcesses(port);
    }
  }

  private isProgressEvent(message: ProcessMessage): boolean {
    return PROGRESS_EVENTS.has(message.event);
  }

  private clearProgressThrottle(processId: string): void {
    const timer = this.progressTimers.get(processId);
    if (timer) {
      clearTimeout(timer);
      this.progressTimers.delete(processId);
    }
    this.progressPending.delete(processId);
    this.progressLastFlush.delete(processId);
  }

  private flushProgress(processId: string): void {
    const timer = this.progressTimers.get(processId);
    if (timer) {
      clearTimeout(timer);
      this.progressTimers.delete(processId);
    }

    const pending = this.progressPending.get(processId);
    if (!pending) return;

    this.progressPending.delete(processId);
    this.progressLastFlush.set(processId, Date.now());
    this.doDispatchMessage(pending);
  }

  private scheduleProgressFlush(processId: string): void {
    if (this.progressTimers.has(processId)) return;

    const last = this.progressLastFlush.get(processId) ?? 0;
    const delay = Math.max(0, PROGRESS_THROTTLE_MS - (Date.now() - last));
    this.progressTimers.set(
      processId,
      setTimeout(() => {
        this.progressTimers.delete(processId);
        this.flushProgress(processId);
      }, delay)
    );
  }

  private async ensureConnection(port: number): Promise<boolean> {
    const connection = this.connections.get(port);
    if (!connection) {
      warn(`[SSEManager] Unknown port: ${port}`);
      return false;
    }

    if (
      connection.isConnected &&
      (connection.connectionMode === "sse" || connection.connectionMode === "polling")
    ) {
      return true;
    }

    return this.connectToServer(port);
  }

  private checkAndDisconnectIfNoActiveProcesses(port: number) {
    if (!this.hasActiveProcessesForPort(port)) {
      this.disconnectFromServer(port);
    }
  }

  connectToServer(port: number): Promise<boolean> {
    return new Promise((resolve) => {
      const connection = this.connections.get(port);
      if (!connection) {
        warn(`[SSEManager] Unknown port: ${port}`);
        resolve(false);
        return;
      }

      this.clearReconnectTimer(port);
      this.setConnectionMode(port, "connecting");

      void this.connectSSE(port).then((sseSuccess) => {
        if (sseSuccess) {
          connection.isConnected = true;
          connection.reconnectAttempts = 0;
          this.setConnectionMode(port, "sse");
          resolve(true);
          return;
        }

        this.startPolling(port);
        connection.isConnected = true;
        connection.reconnectAttempts = 0;
        this.setConnectionMode(port, "polling");
        resolve(true);
      });
    });
  }

  disconnectFromServer(port: number) {
    const connection = this.connections.get(port);
    if (!connection) return;

    this.clearReconnectTimer(port);
    this.teardownTransport(port);
    connection.reconnectAttempts = 0;
    this.setConnectionMode(port, "idle");
  }

  isConnected(port: number): boolean {
    const connection = this.connections.get(port);
    return (
      !!connection?.isConnected &&
      (connection.connectionMode === "sse" || connection.connectionMode === "polling")
    );
  }

  getConnectionModes(): Record<number, ProgressConnectionMode> {
    const state: Record<number, ProgressConnectionMode> = {};
    this.connections.forEach((conn, port) => {
      state[port] = conn.connectionMode;
    });
    return state;
  }

  subscribeConnectionState(callback: (state: Record<number, ProgressConnectionMode>) => void) {
    this.connectionStateListeners.add(callback);
    callback(this.getConnectionModes());
    return () => this.connectionStateListeners.delete(callback);
  }

  private async connectSSE(port: number): Promise<boolean> {
    return new Promise((resolve) => {
      try {
        const sseUrl =
          port === 8059 ? `/api/admin/events/upload-progress` : `/api/python/sse`;

        debug(`[SSEManager] Attempting SSE connection to: ${sseUrl}`);
        const sse = new EventSource(sseUrl, { withCredentials: true });
        const connection = this.connections.get(port)!;
        connection.sse = sse;
        connection.sseOpened = false;

        let settled = false;
        const finish = (success: boolean) => {
          if (settled) return;
          settled = true;
          resolve(success);
        };

        sse.onopen = () => {
          connection.sseOpened = true;
          debug(`[SSEManager] SSE connection opened for port ${port}`);
          finish(true);
        };

        sse.onmessage = (event) => {
          connection.pollFailures = 0;
          try {
            const data = JSON.parse(event.data);

            if (port === 8049) {
              if (data?.success && data?.data?.items) {
                const items = data.data.items || [];
                for (const item of items) {
                  this.processMessage(port, { type: "script_progress", ...item });
                }
                return;
              }
            }

            this.processMessage(port, data);
          } catch (e) {
            warn(`[SSEManager] Error parsing SSE message from port ${port}:`, e);
          }
        };

        sse.onerror = () => {
          if (port !== 8059) {
            warn(`[SSEManager] SSE error for port ${port}`);
          } else {
            debug(`[SSEManager] SSE error for port ${port} (8059)`);
          }

          try {
            sse.close();
          } catch {
            // ignore
          }
          connection.sse = null;

          if (connection.sseOpened) {
            connection.isConnected = false;
            connection.sseOpened = false;
            if (this.hasActiveProcessesForPort(port)) {
              this.scheduleReconnect(port);
            } else {
              this.setConnectionMode(port, "idle");
            }
            return;
          }

          finish(false);
        };

        setTimeout(() => {
          if (!connection.sseOpened) {
            try {
              sse.close();
            } catch {
              // ignore
            }
            connection.sse = null;
            finish(false);
          }
        }, SSE_CONNECT_TIMEOUT_MS);
      } catch (e) {
        warn(`[SSEManager] Error creating SSE connection for port ${port}:`, e);
        resolve(false);
      }
    });
  }

  private startPolling(port: number) {
    const connection = this.connections.get(port)!;

    if (connection.pollTimer) {
      clearInterval(connection.pollTimer);
    }

    const urlBase =
      port === 8059 ? `/api/admin/api/upload/progress` : `/api/python/sse`;

    debug(`[SSEManager] Starting polling fallback for port ${port} at: ${urlBase}`);
    connection.pollFailures = 0;

    connection.pollTimer = setInterval(async () => {
      try {
        const pollUrl = `${urlBase}?since=${connection.lastTimestamp}`;

        const resp = await originalFetch(pollUrl, {
          credentials: "include",
          mode: "cors",
        });

        if (!resp.ok) {
          connection.pollFailures += 1;
          if (connection.pollFailures >= MAX_POLL_FAILURES_BEFORE_RECONNECT) {
            warn(
              `[SSEManager] Polling failed ${connection.pollFailures} times for port ${port}, scheduling reconnect`
            );
            if (connection.pollTimer) {
              clearInterval(connection.pollTimer);
              connection.pollTimer = null;
            }
            connection.isConnected = false;
            this.scheduleReconnect(port);
          }
          return;
        }

        connection.pollFailures = 0;
        const json = await resp.json();

        if (port === 8059) {
          const items = json?.data?.items || [];
          const now = json?.data?.now || Date.now();
          connection.lastTimestamp = now;

          for (const item of items) {
            this.processMessage(port, { type: "upload_progress", ...item });
          }
        } else if (json?.success && json?.data?.items) {
          const items = json.data.items;
          const now = json.data.now || Date.now();
          connection.lastTimestamp = now;

          for (const item of items) {
            this.processMessage(port, { type: "script_progress", ...item });
          }
        }
      } catch (e) {
        connection.pollFailures += 1;
        if (connection.pollFailures >= MAX_POLL_FAILURES_BEFORE_RECONNECT) {
          warn(
            `[SSEManager] Polling error ${connection.pollFailures} times for port ${port}, scheduling reconnect:`,
            e
          );
          if (connection.pollTimer) {
            clearInterval(connection.pollTimer);
            connection.pollTimer = null;
          }
          connection.isConnected = false;
          this.scheduleReconnect(port);
        }
      }
    }, POLL_INTERVAL_MS);
  }

  private processMessage(port: number, data: any) {
    try {
      if (data && data.event && data.process_id && data.type) {
        return this.processDirectMessage(data);
      }

      if (data.success && data.event) {
        return this.processWrappedMessage(data);
      }

      if (port === 8059 && data.type === "upload_progress") {
        const process_id = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const message: ProcessMessage = {
          process_id,
          type: "video_upload",
          event: data.event === "video_ready" ? "process_complete" : "upload_progress",
          text: data.text || `${data.event}: ${data.file || ""}`,
          now: Date.now(),
          data: data,
        };

        processStore.addMessage(message.process_id, message);

        if (data.event === "video_ready") {
          processStore.completeProcess(message.process_id, "complete", data);
        }
      }
    } catch (e) {
      warn(`[SSEManager] Error processing message from port ${port}:`, e);
    }
  }

  private processDirectMessage(data: any) {
    const message: ProcessMessage = {
      process_id: data.process_id,
      type: data.type,
      event: data.event,
      text: data.text,
      now: data.now || Date.now(),
      data: {
        ...(data.data && typeof data.data === "object" ? data.data : {}),
        ...(data.job_id ? { job_id: data.job_id } : {}),
        ...(data.source_user_id ? { source_user_id: data.source_user_id } : {}),
      },
      toast: data.toast,
    };

    if (Date.now() - message.now > 5 * 60 * 1000) {
      debug(`[SSEManager] Ignoring old direct message (age: ${Date.now() - message.now}ms)`);
      return;
    }

    this.dispatchMessage(message);
    this.handleCompletion(message);
  }

  private processWrappedMessage(data: any) {
    const message: ProcessMessage = {
      process_id: data.event.process_id,
      type: data.event.type,
      event: data.event.event,
      text: data.event.text,
      now: data.event.now,
      data: {
        ...(data.data && typeof data.data === "object" ? data.data : {}),
        ...(data.event.job_id ? { job_id: data.event.job_id } : {}),
        ...(data.event.source_user_id ? { source_user_id: data.event.source_user_id } : {}),
      },
      toast: data.event.toast,
    };

    if (Date.now() - message.now > 5 * 60 * 1000) {
      debug(`[SSEManager] Ignoring old message (age: ${Date.now() - message.now}ms)`);
      return;
    }

    this.dispatchMessage(message);
    this.handleCompletion(message);
  }

  private dispatchMessage(message: ProcessMessage) {
    if (this.isProgressEvent(message)) {
      const processId = message.process_id;
      this.progressPending.set(processId, message);

      const now = Date.now();
      const last = this.progressLastFlush.get(processId) ?? 0;
      if (now - last >= PROGRESS_THROTTLE_MS) {
        this.flushProgress(processId);
        return;
      }

      this.scheduleProgressFlush(processId);
      return;
    }

    this.flushProgress(message.process_id);
    this.doDispatchMessage(message);
  }

  private doDispatchMessage(message: ProcessMessage) {
    processStore.addMessage(message.process_id, message);
    this.messageListeners.forEach((listener) => {
      try {
        listener(message);
      } catch (e) {
        warn(`[SSEManager] Error in message listener:`, e);
      }
    });
  }

  private handleCompletion(message: ProcessMessage) {
    const completionEvents = new Set<string>([
      "process_complete",
      "process_timeout",
      "process_cancelled",
      "process_error",
      "script_complete",
      "complete",
      "error",
    ]);

    if (!completionEvents.has(message.event)) return;

    let status: "complete" | "timeout" | "error" = "complete";

    if (message.event === "process_timeout") {
      status = "timeout";
    } else if (message.event === "process_cancelled" || message.event === "process_error" || message.event === "error") {
      status = "error";
    } else if (
      message.event === "process_complete" ||
      message.event === "script_complete" ||
      message.event === "complete"
    ) {
      if (message.data && message.data.script_succeeded === false) {
        status = "error";
      } else if (
        message.data &&
        typeof message.data.return_code === "number" &&
        message.data.return_code !== 0
      ) {
        status = "error";
      } else {
        status = "complete";
      }
    }

    processStore.completeProcess(message.process_id, status as any, message.data);
  }

  scheduleCleanup(_port: number, _delayMs: number = 10000) {
    // Keep connections alive while processes may still be running.
  }

  disconnectAll() {
    this.connections.forEach((_, port) => {
      this.disconnectFromServer(port);
    });

    this.cleanupTimers.forEach((timer) => clearTimeout(timer));
    this.cleanupTimers.clear();
  }

  getStatus() {
    const status: Record<number, boolean> = {};
    this.connections.forEach((conn, port) => {
      status[port] = this.isConnected(port);
    });
    return status;
  }

  subscribe(callback: (message: ProcessMessage) => void) {
    this.messageListeners.add(callback);
    return () => this.messageListeners.delete(callback);
  }
}

function connectionModeNeedsRecovery(mode: ProgressConnectionMode | undefined): boolean {
  return mode === "degraded" || mode === "reconnecting" || mode === "idle";
}

export const sseManager = new SSEManager();
